/* eslint-disable vue/one-component-per-file */
import { defineComponent, h, toRefs } from 'vue';
import { CsTeleport } from '../teleport';
import { PopoverProvider, usePopover, usePopoverContext } from './usePopover';

/** @typedef {'bottom-start'|'top'|'right'|'bottom'|'left'|'top-start'|'top-end'|'right-start'|'right-end'|'bottom-end'|'left-start'|'left-end'} PopoverPlacement */
/** @typedef {'click'|'hover'} PopoverTriggerMode */

/**
 * @typedef {object} PopoverProps
 * @property {PopoverPlacement} [placement='bottom-start'] - The placement of the popover relative to the trigger
 * @property {number} [offset=4] - The offset of the popover from the trigger
 * @property {PopoverTriggerMode} [triggerMode='click'] - The trigger mode for opening the popover ('click' or 'hover')
 * @property {number} [hoverOpenDelay=200] - Delay in ms before opening on hover (only applies when triggerMode='hover')
 * @property {number} [hoverCloseDelay=150] - Delay in ms before closing on hover out (only applies when triggerMode='hover')
 * @property {boolean} [isManual=false] - If true, the popover open and close will be handled by prop isOpen
 * @property {boolean} [isOpen=false] - If true and isManual is true, the popover will open programmatically
 * @property {boolean} [isKeepAlive=false] - If true, the popover will be kept alive when it is closed
 * @property {boolean} [isTeleport=false] - If true, the popover will be teleported to the body
 */

/** @template T; @typedef {import("vue").PropType<T>} PropType<T> */

export const CsPopover = defineComponent({
  name: 'CsPopover',
  props: {
    /** @type {PropType<PopoverProps['placement']>} */
    placement: { type: String, default: 'bottom-start' },
    /** @type {PropType<PopoverProps['offset']>} */
    offset: { type: Number, default: 4 },
    /** @type {PropType<PopoverProps['triggerMode']>} */
    triggerMode: { type: String, default: 'click' },
    /** @type {PropType<PopoverProps['hoverOpenDelay']>} */
    hoverOpenDelay: { type: Number, default: 200 },
    /** @type {PropType<PopoverProps['hoverCloseDelay']>} */
    hoverCloseDelay: { type: Number, default: 150 },
    /** @type {PropType<PopoverProps['isManual']>} */
    isManual: { type: Boolean, default: false },
    /** @type {PropType<PopoverProps['isOpen']>} */
    isOpen: { type: Boolean, default: false },
    /** @type {PropType<PopoverProps['isKeepAlive']>} */
    isKeepAlive: { type: Boolean, default: false },
    /** @type {PropType<PopoverProps['isTeleport']>} */
    isTeleport: { type: Boolean, default: false },
  },
  emits: ['open', 'close', 'click-outside'],
  setup(props, { slots, emit }) {
    const {
      isInside,
      isOpen,
      trigger,
      content,
      floatingStyles,
      floatingPlacement,
      openPopover,
      closePopover,
      scheduleOpenPopover,
      scheduleClosePopover,
    } = usePopover(props, emit);

    PopoverProvider({
      isOpen,
      trigger,
      content,
      floatingStyles,
      floatingPlacement,
      isInside,
      openPopover,
      closePopover,
      scheduleOpenPopover,
      scheduleClosePopover,
      rootProps: toRefs(props),
    });

    return () => {
      return h(
        'div',
        {
          class: 'relative',
          attrs: {
            'data-component': 'CsPopover',
          },
          on: {
            mouseenter: () => {
              isInside.value = true;
            },
            mouseleave: () => {
              isInside.value = false;
            },
          },
        },
        slots.default &&
          slots.default({
            isOpen: isOpen.value,
            open: openPopover,
            close: closePopover,
          })
      );
    };
  },
});

export const CsPopoverTrigger = defineComponent({
  name: 'CsPopoverTrigger',
  emits: ['click'],
  setup(_props, { slots, emit }) {
    const {
      trigger,
      openPopover,
      scheduleOpenPopover,
      scheduleClosePopover,
      rootProps,
    } = usePopoverContext();

    return () => {
      const children = slots.default && slots.default();
      const triggerMode = rootProps.triggerMode.value;

      const eventHandlers =
        triggerMode === 'hover'
          ? {
              mouseenter: () => {
                scheduleOpenPopover(rootProps.hoverOpenDelay.value);
              },
              mouseleave: () => {
                scheduleClosePopover(rootProps.hoverCloseDelay.value);
              },
            }
          : {
              click: e => {
                // Prevent the default click behavior of the trigger
                e.preventDefault();

                openPopover();
                emit('click');
              },
            };

      return h(
        'div',
        {
          ref: trigger,
          class: 'relative inline-flex',
          attrs: {
            'data-component': 'CsPopoverTrigger',
          },
          on: eventHandlers,
        },
        children
      );
    };
  },
});

export const CsPopoverContent = defineComponent({
  name: 'CsPopoverContent',
  props: {
    zIndex: { type: [Number, String], default: 1000 },
  },
  setup(props, { slots, attrs }) {
    const {
      isOpen,
      content,
      floatingStyles,
      rootProps,
      isInside,
      scheduleOpenPopover,
      scheduleClosePopover,
    } = usePopoverContext();

    return () => {
      const children = slots.default && slots.default();
      const triggerMode = rootProps.triggerMode.value;

      const contentNode = h(
        'div',
        {
          ref: content,
          class: 'z-[var(--z-index)]',
          attrs: {
            'data-component': 'CsPopoverContent',
          },
          style: {
            ...floatingStyles.value,
            '--z-index': props.zIndex,
          },
          on: {
            mouseenter: () => {
              isInside.value = true;
              // Cancel any scheduled close when entering content area
              if (triggerMode === 'hover') {
                scheduleOpenPopover(0);
              }
            },
            mouseleave: () => {
              isInside.value = false;
              // Schedule close only in hover mode
              if (triggerMode === 'hover') {
                scheduleClosePopover(rootProps.hoverCloseDelay.value);
              }
            },
          },
          ...attrs,
        },
        children
      );

      const contentNode2 = rootProps.isTeleport.value
        ? h(CsTeleport, {}, [contentNode])
        : contentNode;
      const finalNode = isOpen.value ? contentNode2 : null;

      return finalNode;
    };
  },
});
